import { TippyWithOriginalStyles } from '@upright/ui/common/generic/components/tooltips/default-tippy';
import { useLayoutEffect, useRef, useState } from 'react';
import styled from 'styled-components';

interface ClampedTextCellProps {
  value: string | null;
}

/**
 * View-mode text cell for the site-locations table (PLT-5464). Clamps the value to two lines,
 * truncating the overflow with an ellipsis, and reveals the full text in a hover tooltip — but
 * only when the text is actually clamped, so short values stay tooltip-free.
 *
 * The enclosing table cell has `overflow: hidden`, which would clip a tooltip rendered inside it,
 * so the tooltip is appended to document.body.
 */
export function ClampedTextCell({ value }: ClampedTextCellProps) {
  const textRef = useRef<HTMLSpanElement>(null);
  const [isClamped, setIsClamped] = useState(false);

  // The clamp depends on the rendered width, so re-measure whenever the value changes and on resize.
  useLayoutEffect(() => {
    const el = textRef.current;
    if (!el) return;

    const measure = () => {
      setIsClamped(el.scrollHeight > el.clientHeight);
    };
    measure();

    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => {
      observer.disconnect();
    };
  }, [value]);

  const text = value ?? '';
  const clampedText = <ClampedText ref={textRef}>{text}</ClampedText>;

  if (!isClamped || text === '') return clampedText;

  return (
    <TippyWithOriginalStyles
      interactive={false}
      trigger="mouseenter focus"
      placement="top"
      content={text}
      appendTo={() => document.body}
    >
      {clampedText}
    </TippyWithOriginalStyles>
  );
}

const ClampedText = styled.span`
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  overflow: hidden;
  overflow-wrap: anywhere;
  width: 100%;
  min-width: 0;
`;
